import { useApolloClient } from '@apollo/client';
import { BoardFragmentFragment } from '@apps/www/src/__generated__/graphql';
import useAllowedFeatureCheckEvent from '@apps/www/src/www/hooks/useAllowedFeatureCheckEvent';
import updateBoardMutation from '@apps/www/src/www/queries/updateBoardMutation';
import SVDropdownContent from '@pkgs/shared-client/components/SVDropdownContent';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import AllowedFeature from '@pkgs/shared/enums/AllowedFeature';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import { useState } from 'react';

const SVEditBoardFieldContainer = ({
	board,
	field,
	hasAdminPrivileges,
	children,
	title,
	className,
}: {
	board: BoardFragmentFragment;
	field: 'isPrivate' | 'isHidden';
	hasAdminPrivileges: boolean;
	children?: React.ReactNode;
	title?: string;
	className?: string;
}) => {
	const client = useApolloClient();
	const [isLoading, setIsLoading] = useState(false);
	const isTeamBoard = board.ownershipType === BoardOwnershipType.TEAM;
	const currentValue = field === 'isPrivate' ? board.isPrivate : board.isHidden;

	// Determine which feature to check based on the field
	const featureToCheck =
		field === 'isPrivate' ? AllowedFeature.PRIVATE_BOARD : AllowedFeature.HIDE_BOARD;

	const handleToggle = useAllowedFeatureCheckEvent(
		featureToCheck,
		useEventCallback(async () => {
			if (isLoading) {
				return;
			}

			setIsLoading(true);
			try {
				await client.mutate({
					mutation: updateBoardMutation,
					variables: {
						input: {
							boardID: board._id,
							name: board.name,
							[field]: !currentValue,
						},
					},
				});
			} catch (error) {
				console.error(`Error toggling board ${field}:`, error);
			} finally {
				setIsLoading(false);
			}
		}),
	);

	// Don't show for team boards
	if (isTeamBoard) {
		return null;
	}

	// Don't show if user doesn't have admin privileges
	if (!hasAdminPrivileges) {
		return null;
	}

	return (
		<SVDropdownContent.Links.ItemToggle
			onClick={handleToggle}
			isPressed={currentValue}
			title={title}
			className={className}
			isLoading={isLoading}
		>
			{children}
		</SVDropdownContent.Links.ItemToggle>
	);
};
export default SVEditBoardFieldContainer;
